import React from 'react';
import PropTypes from 'prop-types';
import { cn } from '../../cn';

const SIZE_CLASSES = {
  s: 'h-28',
  m: 'h-56',
  l: 'h-80'
};

const EMPTY_SIZE_CLASSES = {
  s: {
    container: 'py-8',
    iconWrap: 'h-9 w-9 mb-2',
    icon: 'h-5 w-5',
    message: 'text-sm'
  },
  m: {
    container: 'py-10',
    iconWrap: 'h-12 w-12 mb-3',
    icon: 'h-6 w-6',
    message: 'text-sm'
  },
  l: {
    container: 'py-12',
    iconWrap: 'h-16 w-16 mb-4',
    icon: 'h-8 w-8',
    message: 'text-sm'
  }
};

const LoadingContent = ({ size }) => {
  if (size === 's') {
    return (
      <>
        <div className="h-3 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="h-8 bg-gray-200 rounded w-1/2" />
      </>
    );
  }

  if (size === 'm') {
    return (
      <>
        <div className="h-3 bg-gray-200 rounded w-2/5 mb-2" />
        <div className="h-2 bg-gray-100 rounded w-1/3 mb-5" />
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="rounded-lg border border-gray-100 px-3 py-2.5 space-y-2">
              <div className="h-3 bg-gray-200 rounded w-3/4" />
              <div className="h-2 bg-gray-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="h-3 bg-gray-200 rounded w-1/4 mb-2" />
      <div className="h-2 bg-gray-100 rounded w-1/5 mb-5" />
      <div className="flex-1 min-h-[12rem] bg-gray-100 rounded-lg" />
    </>
  );
};

const Placeholder = ({
  size = 'm',
  empty = false,
  icon: Icon,
  illustration,
  message,
  description,
  className = ''
}) => {
  if (empty) {
    const emptySize = EMPTY_SIZE_CLASSES[size] || EMPTY_SIZE_CLASSES.m;

    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center text-center px-2',
          emptySize.container,
          className
        )}
      >
        {illustration ? (
          <div className={cn('flex items-center justify-center', emptySize.iconWrap)}>{illustration}</div>
        ) : Icon ? (
          <div
            className={cn(
              'rounded-full bg-gray-100 flex items-center justify-center text-gray-500',
              emptySize.iconWrap
            )}
          >
            <Icon className={emptySize.icon} aria-hidden="true" />
          </div>
        ) : null}
        {message && (
          <p className={cn('text-gray-600', emptySize.message)}>{message}</p>
        )}
        {description && (
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'bg-white border border-gray-100 rounded-xl p-5 animate-pulse flex flex-col',
        SIZE_CLASSES[size],
        className
      )}
    >
      <LoadingContent size={size} />
    </div>
  );
};

Placeholder.displayName = 'Placeholder';

Placeholder.propTypes = {
  size: PropTypes.oneOf(['s', 'm', 'l']),
  empty: PropTypes.bool,
  icon: PropTypes.elementType,
  illustration: PropTypes.node,
  message: PropTypes.string,
  description: PropTypes.string,
  className: PropTypes.string
};

export default Placeholder;
